/**
 * The only way 2D art reaches the DOM (art §15.3): `artImage()` → `<span class="mc-asset" data-state="reserve|sketch|
 * ready|missing">` and `inkGlyph()` → `<svg class="mc-ink">`. Owner: UI/Visual (F3); the manifest and its ids are F5's
 * (`public/assets/manifest.json`, `src/content/art/manifest.ts`).
 *
 * Lifecycle: reserve (final box, flat vellumAged, 0–300 ms) → sketch (ink outline of the placeholder shape + the
 * subject's «Оттиск», or our built-in emblem, at 40 %) → fetch → reveal (instant if decoded ≤ 100 ms after mount, else
 * a 200 ms ink-bleed through the fibre-noise mask) → ready. Failure: one retry after 2 s, then `missing` stays on the
 * sketch (no toast). Before F5's manifest exists every id is "unknown": the box shows the emblem in full ink tone
 * (`data-state="missing"`, reported by {@link artStats}) so screens stay legible.
 */
import { emblem, type Emblem, type EmblemTone } from '../icons/emblems';

import {
  artUrl as manifestArtUrl, assetEntry, inkHref as manifestInkHref, loadManifest, setManifest,
  type AssetEntry, type AssetManifest,
} from '../../content/art/manifest';

/** F5's manifest entry (art §15.3). */
export type ManifestEntry = AssetEntry;

let state: 'none' | 'loading' | 'ready' | 'absent' = 'none';
let manifestPromise: Promise<boolean> | null = null;
const missing = new Set<string>();
const misuse = new Set<string>();
const listeners = new Set<() => void>();

/** Loads `/assets/manifest.json` once (boot, in parallel with the fonts) through F5's loader. False when absent. */
export function loadArtManifest(base = '/'): Promise<boolean> {
  state = state === 'none' ? 'loading' : state;
  manifestPromise ??= loadManifest(base)
    .then((m) => { state = 'ready'; for (const fn of listeners) fn(); return m.entries.length > 0; })
    .catch(() => { state = 'absent'; return false; });
  return manifestPromise;
}

/** Test / art-lab hook: install a manifest directly. */
export function setArtManifest(entries: AssetEntry[]): void {
  setManifest({ version: 1, generatedAt: 'test', entries } as AssetManifest, '/', { dev: false });
  state = 'ready';
  for (const fn of listeners) fn();
}
export function artEntry(id: string): AssetEntry | null { return (assetEntry(id) as AssetEntry | undefined) ?? null; }
export function onManifest(fn: () => void): () => void { listeners.add(fn); return () => listeners.delete(fn); }

/** F5's `artUrl` (size rule 1 enforced in dev; misuse recorded for `__marg.ui.assets()`). */
export function artUrl(id: string, displayCssPx: number, dprCap = 2): string | null {
  try { return manifestArtUrl(id, displayCssPx, dprCap); } catch (e) { misuse.add(`${id}@${displayCssPx}`); throw e; }
}

/** F5's `inkHref` (size rules 2 and 4 enforced in dev). */
export function inkHref(id: string, displayCssPx: number): string | null {
  try { return manifestInkHref(id, displayCssPx); } catch (e) { misuse.add(`${id}@ink${displayCssPx}`); throw e; }
}

export interface ArtImageOpts {
  /** Display size in CSS px (the box's width; height follows the master's aspect unless `h` is given). */
  size: number;
  h?: number;
  /** Placeholder shape of the sketch outline. */
  shape?: 'roundel' | 'card' | 'square' | 'arch' | 'scene';
  /** The built-in emblem used while the painting is absent (and as the sketch when no ink sprite exists). */
  emblem?: Emblem;
  tone?: EmblemTone;
  priority?: 'high' | 'low' | 'auto';
  alt?: string;
  class?: string;
}

const SKETCH_AFTER_MS = 300;

/** A sized art box that walks reserve → sketch → ready (or missing). */
export function artImage(id: string, opts: ArtImageOpts): HTMLSpanElement {
  const box = document.createElement('span');
  box.className = `mc-asset${opts.class ? ` ${opts.class}` : ''}`;
  box.dataset.art = id;
  box.dataset.shape = opts.shape ?? 'square';
  const w = opts.size;
  const e = artEntry(id);
  const h = opts.h ?? (e ? Math.round(w * e.h / e.w) : w);
  box.style.inlineSize = `${w}px`;
  box.style.blockSize = `${h}px`;
  if (!opts.alt) box.setAttribute('aria-hidden', 'true'); else { box.setAttribute('role', 'img'); box.setAttribute('aria-label', opts.alt); }

  if (document.documentElement.dataset.artSource === 'procedural') {
    box.dataset.state = 'ready';
    box.appendChild(emblem(opts.emblem ?? 'quill', { size: Math.min(w, h), tone: opts.tone ?? 'ink' }));
    return box;
  }

  const sketch = (full: boolean): void => {
    if (box.querySelector('.mc-asset__sketch')) return;
    const em = emblem(opts.emblem ?? 'quill', { size: Math.min(w, h), tone: full ? (opts.tone ?? 'ink') : 'placeholder', class: 'mc-asset__sketch' });
    box.appendChild(em);
  };

  const url = artUrl(id, Math.max(28, w));
  if (!url) {
    // Unknown to the manifest (or no manifest yet): the emblem stands in at full tone, and the id is reported.
    box.dataset.state = 'missing';
    missing.add(id);
    sketch(true);
    if (state === 'none' || state === 'loading') onManifest(() => { if (artEntry(id) && box.isConnected) { missing.delete(id); box.replaceWith(artImage(id, opts)); } });
    return box;
  }

  box.dataset.state = 'reserve';
  const mountedAt = performance.now();
  const sketchTimer = window.setTimeout(() => { if (box.dataset.state === 'reserve') { box.dataset.state = 'sketch'; sketch(false); } }, SKETCH_AFTER_MS);
  const img = new Image();
  img.decoding = 'async';
  img.alt = '';
  img.draggable = false;
  (img as HTMLImageElement & { fetchPriority?: string }).fetchPriority = opts.priority ?? 'auto';
  img.loading = e?.loadTrigger === 'boot' || e?.loadTrigger === 'route' ? 'eager' : 'lazy';

  let tries = 0;
  const load = (): void => {
    tries++;
    img.src = url;
    img.decode().then(() => {
      window.clearTimeout(sketchTimer);
      box.appendChild(img);
      const fast = performance.now() - mountedAt <= 100 || document.documentElement.dataset.motion === 'min' || document.documentElement.dataset.motion === 'reduced';
      box.dataset.state = 'ready';
      if (!fast) {
        box.classList.add('is-revealing');
        box.animate([{ '--reveal': 0 } as Keyframe, { '--reveal': 1 } as Keyframe], { duration: 200, easing: 'ease-out' })
          .finished.then(() => box.classList.remove('is-revealing'), () => box.classList.remove('is-revealing'));
      }
    }).catch(() => {
      if (tries < 2) { window.setTimeout(load, 2000); return; }
      window.clearTimeout(sketchTimer);
      box.dataset.state = 'missing';
      box.dataset.artMissing = '';
      missing.add(id);
      sketch(false);
    });
  };
  load();
  return box;
}

export interface InkGlyphOpts { size: number; tone?: 'ink' | 'element' | 'muted' | 'night' | 'placeholder' | 'gold' | 'vermilion'; emblem?: Emblem; label?: string }

/** The «Оттиск» of a content id (13–27 px), or the built-in emblem when the sprite is not in the manifest yet. */
export function inkGlyph(id: string, opts: InkGlyphOpts): SVGSVGElement {
  if (document.documentElement.dataset.artSource === 'procedural') {
    const el = emblem(opts.emblem ?? 'quill', { size: opts.size, tone: opts.tone === 'element' ? 'ink' : opts.tone ?? 'ink', class: 'mc-ink' });
    el.dataset.art = id;
    if (opts.label) { el.removeAttribute('aria-hidden'); el.setAttribute('role', 'img'); el.setAttribute('aria-label', opts.label); }
    return el;
  }
  const href = inkHref(id, opts.size);
  const tone = opts.tone ?? 'ink';
  if (!href) {
    missing.add(id);
    const em = emblem(opts.emblem ?? 'quill', { size: opts.size, tone: tone === 'element' ? 'ink' : tone as EmblemTone, class: 'mc-ink' });
    em.dataset.art = id;
    if (opts.label) { em.removeAttribute('aria-hidden'); em.setAttribute('role', 'img'); em.setAttribute('aria-label', opts.label); }
    return em;
  }
  const ns = 'http://www.w3.org/2000/svg';
  const el = document.createElementNS(ns, 'svg');
  el.setAttribute('class', 'mc-ink');
  el.setAttribute('width', String(opts.size));
  el.setAttribute('height', String(opts.size));
  el.dataset.art = id;
  el.dataset.tone = tone;
  if (opts.label) { el.setAttribute('role', 'img'); el.setAttribute('aria-label', opts.label); } else el.setAttribute('aria-hidden', 'true');
  const use = document.createElementNS(ns, 'use');
  use.setAttribute('href', href);
  el.appendChild(use);
  return el;
}

/** `__marg.ui.assets()`: ids that fell back to the sketch, and size-rule violations. */
export function artStats(): { manifest: string; missing: string[]; misuse: string[] } {
  return { manifest: state, missing: [...missing].sort(), misuse: [...misuse].sort() };
}

/** Picks the smallest variant ≥ `px` source pixels (else the largest) of a manifest entry → a CSS `url()`. */
function cssUrlOf(id: string, px: number): string | null {
  const e = artEntry(id);
  if (!e || e.variants.length === 0) return null;
  const v = [...e.variants].sort((a, b) => a.w - b.w).find((x) => x.w >= px) ?? e.variants[e.variants.length - 1];
  return `url("/${e.path.replace(/^\//, '')}.${v.w}.webp")`;
}

/**
 * Applies F5's painted UI layer when the manifest has it (art §15.2 `ui/frames`, plus the owner-requested
 * `ui/ornaments` extras): 9-slice frames (`--ui-frame-*`, slice from the manifest), wax seals, the banderole ribbon.
 * `<html data-art>` lists the painted families present so the kit CSS switches from the vector / baked fallbacks.
 */
export function applyPaintedFrames(root: HTMLElement = document.documentElement): void {
  const families = new Set((root.dataset.art ?? '').split(' ').filter(Boolean));
  const frames: Array<[string, string]> = [
    ['gilt', 'gilt'], ['plate_band', 'plate-band'], ['plate_card', 'plate-card'], ['reward', 'reward'],
    ['r1', 'r1'], ['r2', 'r2'], ['r3', 'r3'], ['r4', 'r4'], ['r5', 'r5'], ['set', 'set'],
  ];
  for (const [name, css] of frames) {
    const id = `ui/frames/${name}`;
    const e = artEntry(id);
    const url = cssUrlOf(id, 288);
    if (!e || !url) continue;
    root.style.setProperty(`--ui-frame-${css}-painted`, url);
    root.style.setProperty(`--ui-frame-${css}-painted-slice`, `${Math.round((e.slice ?? 0.25) * 100)}%`);
    families.add('frames');
  }
  const ornaments: Array<[string, string, number]> = [
    ['seal_blank', '--ui-seal-blank', 128], ['seal_swords', '--ui-seal-swords', 128], ['seal_paw', '--ui-seal-paw', 128],
    ['ribbon_banderole', '--ui-ribbon-banderole', 512], ['flourish_red', '--ui-flourish-red', 384], ['flourish_blue', '--ui-flourish-blue', 384],
  ];
  for (const [name, cssVar, px] of ornaments) {
    const url = cssUrlOf(`ui/ornaments/${name}`, px);
    if (!url) continue;
    root.style.setProperty(cssVar, url);
    families.add(name.startsWith('seal') ? 'seals' : name.startsWith('ribbon') ? 'ribbons' : 'flourishes');
  }
  const vellum = cssUrlOf('ui/textures/vellum_fine', 512);
  if (vellum) { root.style.setProperty('--mc-vellum-painted', vellum); families.add('vellum'); }
  root.dataset.art = [...families].sort().join(' ');
}
